"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import {
  MapPin,
  Loader2,
  Search,
  Navigation,
  X,
  Check,
  ChevronDown,
  Clock,
  Star,
  Map,
} from "lucide-react";

export default function LocationPicker({
  value,
  onChange,
  setLocationPlaceId,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const autocompleteServiceRef = useRef(null);

  const API_KEY = process.env.REACT_APP_GOOGLEMAP_API;

  // Load recent searches from localStorage with error handling
  useEffect(() => {
    try {
      const saved = localStorage.getItem("locationPicker_recentSearches");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate the data structure and filter out any corrupted entries
        const validSearches = parsed.filter(
          (item) =>
            item &&
            typeof item === "object" &&
            typeof item.query === "string" &&
            !item.icon // Remove any entries that have icon property (corrupted data)
        );
        setRecentSearches(validSearches);

        // If we had to filter out corrupted data, save the cleaned version
        if (validSearches.length !== parsed.length) {
          localStorage.setItem(
            "locationPicker_recentSearches",
            JSON.stringify(validSearches)
          );
        }
      }
    } catch (e) {
      console.error(
        "Error loading recent searches, clearing corrupted data:",
        e
      );
      // Clear corrupted localStorage data
      localStorage.removeItem("locationPicker_recentSearches");
      setRecentSearches([]);
    }
  }, []);

  // Save recent searches to localStorage
  const saveRecentSearches = useCallback((searches) => {
    try {
      localStorage.setItem(
        "locationPicker_recentSearches",
        JSON.stringify(searches)
      );
    } catch (e) {
      console.error("Error saving recent searches:", e);
    }
  }, []);

  // Debounced search
  const debouncedSearch = useCallback((query) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (query.trim() && query.length >= 2) {
        searchPlaces(query);
      } else {
        setSearchResults([]);
      }
    }, 300);
  }, []);

  // Initialize map when modal opens - FIXED VERSION
  useEffect(() => {
    if (isOpen && mapLoaded && mapRef.current) {
      // Clean up existing map instance if it exists
      if (mapInstanceRef.current) {
        if (markerRef.current) {
          markerRef.current.setMap(null);
          markerRef.current = null;
        }
        mapInstanceRef.current = null;
      }

      // Always initialize when modal opens
      setTimeout(() => {
        initializeMap();
      }, 100); // Small delay to ensure DOM is ready
    }
  }, [isOpen, mapLoaded]);

  // Load Google Maps API
  useEffect(() => {
    if (!API_KEY) {
      console.error(
        "Google Maps API key not found. Please add REACT_APP_GOOGLEMAP_API to your .env file"
      );
      return;
    }

    if (!window.google) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setMapLoaded(true);
        // Initialize autocomplete service when API loads
        if (window.google) {
          autocompleteServiceRef.current =
            new window.google.maps.places.AutocompleteService();
        }
      };
      script.onerror = () => console.error("Failed to load Google Maps API");
      document.head.appendChild(script);
    } else {
      setMapLoaded(true);
      if (window.google && !autocompleteServiceRef.current) {
        autocompleteServiceRef.current =
          new window.google.maps.places.AutocompleteService();
      }
    }
  }, [API_KEY]);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
        setShowDropdown(true);
      }, 100);
    }
  }, [isOpen]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    const visibleRecentSearches = !searchQuery ? recentSearches : [];
    const totalOptions =
      visibleRecentSearches.length + searchResults.length + 1; // +1 for current location

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < totalOptions - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > -1 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();

        // If no option is selected (selectedIndex is -1), just search with current query
        if (selectedIndex === -1) {
          if (searchQuery.trim()) {
            searchPlaces(searchQuery);
          }
          return;
        }

        if (selectedIndex === 0) {
          getCurrentLocation();
        } else if (
          selectedIndex <= visibleRecentSearches.length &&
          visibleRecentSearches.length > 0
        ) {
          const recentIndex = selectedIndex - 1;
          if (recentIndex >= 0 && recentIndex < visibleRecentSearches.length) {
            const recent = visibleRecentSearches[recentIndex];
            if (recent && recent.query) {
              setSearchQuery(recent.query);
              searchPlaces(recent.query);
            }
          }
        } else {
          const resultIndex = selectedIndex - visibleRecentSearches.length - 1;
          if (resultIndex >= 0 && resultIndex < searchResults.length) {
            const result = searchResults[resultIndex];
            if (result) {
              handleSearchResultClick(result);
            }
          }
        }
        break;
      case "Escape":
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const initializeMap = () => {
    if (!window.google || !mapRef.current) return;

    const center = value
      ? { lat: value.lat, lng: value.lng }
      : { lat: 30.2672, lng: -97.7431 };

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: value ? 16 : 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "on" }],
        },
      ],
    });

    // Add click listener
    mapInstanceRef.current.addListener("click", handleMapClick);

    // Add existing marker if value exists
    if (value) {
      addMarker(value.lat, value.lng, value.address);
    }
  };

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    reverseGeocode(lat, lng);
  };

  const addMarker = (lat, lng, address = null) => {
    if (!mapInstanceRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Add new marker
    markerRef.current = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstanceRef.current,
      draggable: true,
      animation: window.google.maps.Animation.DROP,
      icon: {
        url:
          "data:image/svg+xml;charset=UTF-8," +
          encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 2C10.486 2 6 6.486 6 12C6 20 16 30 16 30C16 30 26 20 26 12C26 6.486 21.514 2 16 2ZM16 16C13.791 16 12 14.209 12 12C12 9.791 13.791 8 16 8C18.209 8 20 9.791 20 12C20 14.209 18.209 16 16 16Z" fill="#7B189F"/>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 32),
      },
    });

    // Add drag listener
    markerRef.current.addListener("dragend", (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      reverseGeocode(lat, lng);
    });

    // Center map on marker
    mapInstanceRef.current.panTo({ lat, lng });
    mapInstanceRef.current.setZoom(16);

    // Set selected location
    setSelectedLocation({ lat, lng, address });
    setShowConfirmation(true);
  };

  const reverseGeocode = async (lat, lng) => {
    if (!window.google) return;

    setIsLoading(true);
    const geocoder = new window.google.maps.Geocoder();

    try {
      const response = await geocoder.geocode({ location: { lat, lng } });
      if (response.results[0]) {
        const address = response.results[0].formatted_address;
        setSelectedLocation({ lat, lng, address });
      }
    } catch (error) {
      console.error("Geocoding failed:", error);
      setSelectedLocation({ lat, lng, address: "Selected Location" });
    } finally {
      setIsLoading(false);
    }
  };

  const searchPlaces = async (query) => {
    if (!query.trim() || !window.google || !autocompleteServiceRef.current)
      return;

    setIsLoading(true);

    try {
      const request = {
        input: query,
        types: ["establishment", "geocode"],
        // Restrict to Nepal, change as needed
      };

      autocompleteServiceRef.current.getPlacePredictions(
        request,
        (predictions, status) => {
          setIsLoading(false);

          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            const locations = predictions.slice(0, 6).map((prediction) => ({
              placeId: prediction.place_id,
              address: prediction.description,
              name: prediction.structured_formatting.main_text,
              secondary: prediction.structured_formatting.secondary_text,
              types: prediction.types,
            }));
            setSearchResults(locations);
          } else {
            setSearchResults([]);
          }
        }
      );
    } catch (error) {
      console.error("Search error:", error);
      setIsLoading(false);
      setSearchResults([]);
    }
  };

  const getPlaceDetails = (placeId) => {
    return new Promise((resolve, reject) => {
      if (!window.google || !mapInstanceRef.current) {
        reject(new Error("Google Maps not loaded"));
        return;
      }

      const service = new window.google.maps.places.PlacesService(
        mapInstanceRef.current
      );
      const request = {
        placeId: placeId,
        fields: ["name", "geometry", "formatted_address", "place_id", "rating"],
      };

      service.getDetails(request, (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          resolve({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            address: place.formatted_address,
            name: place.name,
            rating: place.rating,
            placeId: place.place_id,
          });
        } else {
          reject(new Error("Place details not found"));
        }
      });
    });
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSelectedIndex(-1);

    if (value.trim()) {
      debouncedSearch(value);
      setShowDropdown(true);
    } else {
      setSearchResults([]);
      setShowDropdown(recentSearches.length > 0);
    }
  };

  const handleSearchResultClick = async (location) => {
    setSearchQuery(location.name);
    setShowDropdown(false);
    setIsLoading(true);

    try {
      const placeDetails = await getPlaceDetails(location.placeId);

      setLocationPlaceId(location.placeId);
      // Save to recent searches (just store the query string)
      const newRecent = { query: location.name };
      const updatedRecent = [
        newRecent,
        ...recentSearches.filter((item) => item.query !== location.name),
      ].slice(0, 3);
      setRecentSearches(updatedRecent);
      saveRecentSearches(updatedRecent);

      // Add marker to map
      addMarker(placeDetails.lat, placeDetails.lng, placeDetails.address);
    } catch (error) {
      console.error("Error getting place details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser.");
      return;
    }

    setIsLoading(true);
    setShowDropdown(false);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        addMarker(latitude, longitude);
        reverseGeocode(latitude, longitude);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error getting location:", error);
        setIsLoading(false);
        alert(
          "Unable to get your current location. Please search for your address."
        );
      }
    );
  };

  const confirmSelection = () => {
    if (selectedLocation) {
      console.log("selectedLocation", selectedLocation);
      onChange(selectedLocation);
      setIsOpen(false);
      setShowConfirmation(false);
      setSelectedLocation(null);
    }
  };

  // FIXED: Properly clean up when closing modal
  const closeModal = () => {
    setIsOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
    setShowConfirmation(false);
    setSelectedLocation(null);
    setSelectedIndex(-1);

    // Clean up map instance and marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    if (mapInstanceRef.current) {
      mapInstanceRef.current = null;
    }
  };

  const handleSearchFocus = () => {
    setShowDropdown(true);
  };

  if (!API_KEY) {
    return (
      <div className="w-full px-4 py-3 border border-red-200 rounded-lg bg-red-50">
        <p className="text-red-600 text-sm">
          Google Maps API key not configured. Please add REACT_APP_GOOGLEMAP_API
          to your environment variables.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Input Field */}
      <div className="relative">
        <input
          type="text"
          value={value?.address || ""}
          placeholder="Click to select your business location"
          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent pr-10 cursor-pointer bg-white"
          readOnly
          onClick={() => setIsOpen(true)}
        />
        <MapPin
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          size={20}
        />
      </div>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-[#7B189F] to-[#9333EA] text-white">
              <div>
                <h3 className="text-xl font-bold">
                  Find Your Business Location
                </h3>
                <p className="text-sm text-white/80 mt-1">
                  Search and click on the map to pinpoint your exact location
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Enhanced Search Section with Dropdown */}
            <div className="p-4 border-b bg-gray-50 relative" ref={dropdownRef}>
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10"
                    size={18}
                  />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onFocus={handleSearchFocus}
                    onKeyDown={handleKeyDown}
                    placeholder="Search for your business address or nearby landmarks..."
                    className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
                    autoComplete="off"
                  />
                  <ChevronDown
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 transition-transform ${
                      showDropdown ? "rotate-180" : ""
                    }`}
                    size={18}
                  />

                  {/* Dropdown */}
                  {showDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                      {/* Current Location Option */}
                      <div
                        className={`flex items-center gap-3 px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors border-b border-gray-100 ${
                          selectedIndex === 0 ? "bg-blue-50" : ""
                        }`}
                        onClick={getCurrentLocation}
                      >
                        <div className="p-2 bg-blue-100 rounded-full">
                          <Navigation size={14} className="text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            Use Current Location
                          </div>
                          <div className="text-sm text-gray-500">
                            Detect your location automatically
                          </div>
                        </div>
                        {isLoading && (
                          <Loader2
                            size={16}
                            className="animate-spin text-blue-600 ml-auto"
                          />
                        )}
                      </div>

                      {/* Recent Searches */}
                      {!searchQuery && recentSearches.length > 0 && (
                        <>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100">
                            RECENT SEARCHES
                          </div>
                          {recentSearches.map((recent, index) => {
                            return (
                              <div
                                key={index}
                                className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                                  selectedIndex === index + 1
                                    ? "bg-gray-50"
                                    : ""
                                }`}
                                onClick={() => {
                                  setSearchQuery(recent.query);
                                  searchPlaces(recent.query);
                                }}
                              >
                                <Clock size={16} className="text-gray-400" />
                                <div className="font-medium text-gray-700">
                                  {recent.query}
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}

                      {/* Search Results */}
                      {searchResults.length > 0 && (
                        <>
                          {(!searchQuery || recentSearches.length > 0) && (
                            <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100">
                              SEARCH RESULTS
                            </div>
                          )}
                          {searchResults.map((location, index) => {
                            const resultIndex =
                              index + recentSearches.length + 1;
                            return (
                              <div
                                key={location.placeId}
                                className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                                  selectedIndex === resultIndex
                                    ? "bg-gray-50"
                                    : ""
                                }`}
                                onClick={() =>
                                  handleSearchResultClick(location)
                                }
                              >
                                <div className="p-2 bg-green-100 rounded-full mt-0.5">
                                  <MapPin
                                    size={14}
                                    className="text-green-600"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <div className="font-medium text-gray-900 truncate">
                                      {location.name}
                                    </div>
                                  </div>
                                  <div className="text-sm text-gray-600 truncate">
                                    {location.address}
                                  </div>
                                  {location.secondary && (
                                    <div className="text-xs text-gray-400 mt-1">
                                      {location.secondary}
                                    </div>
                                  )}
                                </div>
                                <Map size={16} className="text-gray-400 mt-1" />
                              </div>
                            );
                          })}
                        </>
                      )}

                      {/* Loading State */}
                      {isLoading &&
                        searchResults.length === 0 &&
                        searchQuery && (
                          <div className="flex items-center justify-center py-8">
                            <div className="flex items-center gap-3 text-gray-500">
                              <Loader2 size={20} className="animate-spin" />
                              <span>Searching locations...</span>
                            </div>
                          </div>
                        )}

                      {/* No Results */}
                      {!isLoading &&
                        searchResults.length === 0 &&
                        searchQuery &&
                        searchQuery.length >= 2 && (
                          <div className="flex items-center justify-center py-8">
                            <div className="text-center text-gray-500">
                              <Search
                                size={24}
                                className="mx-auto mb-2 opacity-50"
                              />
                              <div className="font-medium">
                                No locations found
                              </div>
                              <div className="text-sm">
                                Try a different search term
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Map Container */}
            <div className="flex-1 relative overflow-hidden">
              {mapLoaded ? (
                <div ref={mapRef} className="w-full h-full min-h-[400px]" />
              ) : (
                <div className="w-full h-full min-h-[400px] bg-gray-100 flex items-center justify-center">
                  <div className="text-center">
                    <Loader2
                      size={48}
                      className="mx-auto text-gray-400 mb-4 animate-spin"
                    />
                    <p className="text-gray-600 font-medium">
                      Loading Google Maps...
                    </p>
                  </div>
                </div>
              )}

              {/* Loading overlay */}
              {isLoading && (
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                  <div className="bg-white rounded-lg p-4 flex items-center gap-3">
                    <Loader2
                      size={20}
                      className="animate-spin text-[#7B189F]"
                    />
                    <span className="text-sm font-medium">Processing...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Confirmation Panel */}
            {showConfirmation && selectedLocation && (
              <div className="p-4 bg-green-50 border-t border-green-200">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <Check size={16} className="text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-green-900">
                      Perfect! Location Selected
                    </div>
                    <div className="text-sm text-green-700 mt-1">
                      {selectedLocation.address}
                    </div>
                    <div className="text-xs text-green-600 mt-1">
                      Coordinates: {selectedLocation.lat.toFixed(6)},{" "}
                      {selectedLocation.lng.toFixed(6)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="p-4 bg-gray-50 border-t flex justify-between items-center">
              <div className="text-sm text-gray-500">
                {selectedLocation
                  ? "Location ready to confirm"
                  : "Search above or click on the map to select your business location"}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSelection}
                  disabled={!selectedLocation}
                  className="px-6 py-2 bg-[#7B189F] text-white rounded-lg hover:bg-[#6A1587] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
                >
                  <Check size={16} />
                  Confirm Location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
